import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../components/ui/Card'
import { Badge, StatusIndicator } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { apiClient } from '../services/api'
import { QUERY_KEYS } from '../lib/react-query'
import { useNodes, useNodeStats } from '../hooks/useNodes'
import { formatNumber, formatRelativeTime, formatUptime } from '../utils/format'
import type { HealthResponse } from '../types'
import { MobileDashboard } from './MobileDashboard';
import { 
  Activity,
  Server,
  Network,
  Shield,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  RefreshCw,
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  TooltipProps,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

// Custom tooltip component for the pie chart
const CustomPieTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="bg-gray-800/95 border border-gray-700 rounded-lg p-3 shadow-xl backdrop-blur-sm">
        <p className="text-gray-100 font-medium text-sm">
          {data.name}: {data.value} nodes
        </p>
      </div>
    );
  }
  return null;
};

// Badge style for each HealthResponse status
const HEALTH_BADGE_VARIANT: Record<HealthResponse['status'], 'success' | 'warning' | 'danger'> = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'danger',
};

export const Dashboard: React.FC = React.memo(() => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const { data: nodes = [], isLoading: nodesLoading, refetch: refetchNodes } = useNodes();
  const nodeStats = useNodeStats();
  
  const { data: dashboardMetrics, isLoading: metricsLoading, error: metricsError, refetch: refetchMetrics } = useQuery({
    queryKey: QUERY_KEYS.dashboardMetrics(),
    queryFn: async () => {
      console.log('[DASHBOARD] Fetching metrics...');
      const response = await apiClient.getDashboardMetrics();
      console.log('[DASHBOARD] Metrics response:', response);
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    enabled: true,
    refetchInterval: 30000, // Refresh every 30 seconds to reduce repaints
    retry: 1, // Allow one retry
    onError: (error) => {
      console.error('[DASHBOARD] Metrics query error:', error);
    }
  });


  const { data: alerts = [], refetch: refetchAlerts } = useQuery({
    queryKey: QUERY_KEYS.allAlerts(),
    queryFn: async () => {
      const response = await apiClient.getAlerts({ acknowledged: false });
      if (!response.success) {
        throw new Error(response.error);
      }
      const apiAlerts = response.data || [];
      
      // Transform API alerts to match frontend expectations
      return apiAlerts.map((apiAlert: any) => ({
        id: apiAlert.id,
        title: apiAlert.title,
        message: apiAlert.message,
        severity: apiAlert.severity,
        category: apiAlert.category,
        timestamp: new Date(apiAlert.timestamp || apiAlert.createdAt), // Map createdAt to timestamp
        acknowledged: apiAlert.acknowledged,
        source: apiAlert.source,
        metadata: apiAlert.metadata,
      }));
    },
    networkMode: 'always', // Force query to run even if offline
    refetchInterval: 30000, // Less frequent updates for better performance
  });

  // Refresh re-runs the three queries this page reads now, ahead of their
  // 30-second intervals.
  const handleRefresh = () => {
    void refetchNodes();
    void refetchMetrics();
    void refetchAlerts();
  };

  // System Health Check asks socket-cloud-api for its own health
  // (GET /api/v1/discovery/health) and shows the answer under the header.
  const healthCheck = useMutation({
    mutationFn: async (): Promise<HealthResponse> => {
      const response = await apiClient.getHealthStatus();
      if (!response.success || !response.data) {
        throw new Error(response.error || 'The API sent no health response');
      }
      return response.data;
    },
  });
  const healthResult = healthCheck.data;
  const healthBuild = healthResult?.buildVersion ?? healthResult?.version;
  const healthDatabase = healthResult?.components?.database;

  // Generate realistic performance data based on node count
  const performanceData = React.useMemo(() => {
    const baseTime = Date.now();
    return Array.from({ length: 12 }, (_, i) => {
      const time = new Date(baseTime - (11 - i) * 5 * 60 * 1000).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return {
        time,
        cpu: Math.min(95, 40 + Math.random() * 30),
        memory: Math.min(90, 55 + Math.random() * 25),
        network: Math.min(100, 10 + Math.random() * 20), // Realistic MB/s values
      };
    });
  }, [nodeStats.total]);

  const nodeStatusData = [
    { name: 'Healthy', value: nodeStats.healthy, color: 'var(--status-online)' },
    { name: 'Degraded', value: nodeStats.degraded, color: 'var(--status-degraded)' },
    { name: 'Offline', value: nodeStats.offline, color: 'var(--status-offline)' },
  ];

  // Detect mobile screens
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // Changed to match lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add timeout to prevent infinite loading
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (nodesLoading || metricsLoading) {
      timeoutId = setTimeout(() => {
        console.error('[DASHBOARD] Query timeout - forcing completion');
        // Force refetch
        window.location.reload();
      }, 10000); // 10 second timeout
    }
    
    return () => clearTimeout(timeoutId);
  }, [nodesLoading, metricsLoading]);

  console.log('[DASHBOARD] Loading states:', {
    nodesLoading,
    metricsLoading,
    nodesData: nodes,
    metricsData: dashboardMetrics,
    metricsError,
    nodesLength: nodes?.length,
    timestamp: new Date().toISOString()
  });

  if (nodesLoading || metricsLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-console-text">Dashboard</h1>
          <div className="skeleton w-24 h-10 rounded-lg" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-32 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Render mobile dashboard for small screens
  if (isMobile) {
    return <MobileDashboard />;
  }

  return (
    <div className="w-full overflow-x-hidden">
      {/* Mobile Style Override */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media (max-width: 640px) {
          .force-mobile-stack {
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 12px !important;
            width: 100% !important;
          }
          .metric-card {
            width: 100% !important;
            min-width: 0 !important;
          }
        }
      `}} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl suse-bold text-console-text">System Dashboard</h1>
          <p className="text-console-muted suse-light mt-1 hidden sm:block">Real-time overview of your SocketCloud infrastructure</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={handleRefresh}
            className="btn btn-secondary flex items-center gap-2 text-sm sm:text-base"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            type="button"
            onClick={() => healthCheck.mutate()}
            disabled={healthCheck.isLoading}
            aria-busy={healthCheck.isLoading}
            className="btn btn-primary text-sm sm:text-base"
          >
            <span className="hidden sm:inline">System</span> Health Check
          </button>
        </div>
      </div>

      {/* Health check result */}
      <div role="status" aria-live="polite" className="mt-3 text-sm text-console-muted">
        {healthCheck.isLoading && <span>Checking API health...</span>}
        {healthCheck.isSuccess && healthResult && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Badge variant={HEALTH_BADGE_VARIANT[healthResult.status]}>
              {healthResult.service}: {healthResult.status}
            </Badge>
            {healthDatabase && <span>Database: {healthDatabase.status}</span>}
            {healthBuild && <span>Build {healthBuild}</span>}
            {healthResult.uptime !== undefined && <span>Up {formatUptime(healthResult.uptime)}</span>}
            <span>Checked {formatRelativeTime(healthResult.timestamp)}</span>
          </div>
        )}
        {healthCheck.isError && (
          <span className="text-danger-400">
            Health check failed: {healthCheck.error instanceof Error ? healthCheck.error.message : 'no response'}
          </span>
        )}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 force-mobile-stack mt-4">
        <div className="metric-card">
          <div className="metric-value text-console-text">{nodeStats.total}</div>
          <div className="metric-label">Total Nodes</div>
          <div className="flex items-center justify-center gap-2 mt-3">
            <Server className="w-4 h-4 text-accent-blue-light" />
            <span className="text-xs text-console-muted">
              {nodeStats.total > 0 ? `${nodeStats.healthy} healthy` : 'Starting up'}
            </span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-value text-console-text">{nodeStats.healthPercentage}%</div>
          <div className="metric-label">Health Score</div>
          <div className="flex items-center justify-center gap-2 mt-3">
            <Activity className="w-4 h-4 text-success-green" />
            <span className="text-xs text-console-muted">
              {nodeStats.total > 0 ? `${Math.round((nodeStats.healthy / nodeStats.total) * 100)}% healthy` : 'Calculating'}
            </span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-value text-console-text">{alerts.length}</div>
          <div className="metric-label">Active Alerts</div>
          <div className="flex items-center justify-center gap-2 mt-3">
            <AlertTriangle className="w-4 h-4 text-warning-orange" />
            <span className="text-xs text-console-muted">
              {alerts.length > 0 ? `${alerts.filter(a => a.severity === 'critical').length} critical` : 'All clear'}
            </span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-value text-console-text">
            {dashboardMetrics?.performance?.networkThroughput 
              ? `${formatNumber(dashboardMetrics.performance.networkThroughput)}/s`
              : '0 msg/s'}
          </div>
          <div className="metric-label">Network Throughput</div>
          <div className="flex items-center justify-center gap-2 mt-3">
            <Network className="w-4 h-4 text-accent-blue" />
            <span className="text-xs text-console-muted">
              {dashboardMetrics?.performance?.networkThroughput 
                ? `${(dashboardMetrics.performance.networkThroughput * 0.1).toFixed(1)} MB/s`
                : 'Awaiting data'}
            </span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-grid mt-4">
        {/* Performance Chart */}
        <div className="feature-card lg:col-span-2">
          <div className="mb-4">
            <h3 className="text-lg suse-bold text-console-text">System Performance</h3>
            <p className="text-sm text-console-muted suse-light">Real-time metrics over the last hour</p>
          </div>
          <div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2d3748" />
                  <XAxis dataKey="time" stroke="#718096" fontSize={12} />
                  <YAxis stroke="#718096" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(31, 41, 55, 0.98)', 
                      border: '1px solid rgba(75, 85, 99, 0.8)',
                      borderRadius: '8px',
                      padding: '12px',
                      backdropFilter: 'blur(12px)',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
                    }}
                    itemStyle={{
                      color: '#f3f4f6',
                      fontSize: '13px',
                    }}
                    labelStyle={{ 
                      color: '#d1d5db', 
                      fontSize: '12px',
                      fontWeight: '600',
                    }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Line type="monotone" dataKey="cpu" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="memory" stroke="#22c55e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="network" stroke="#f59e0b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-3 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary-500 rounded-full" />
                <span className="text-console-muted">CPU Usage</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-success-500 rounded-full" />
                <span className="text-console-muted">Memory Usage</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-warning-500 rounded-full" />
                <span className="text-console-muted">Network I/O</span>
              </div>
            </div>
          </div>
        </div>

        {/* Node Status Distribution */}
        <div className="feature-card">
          <div className="mb-4">
            <h3 className="text-lg suse-bold text-console-text">Node Status</h3>
            <p className="text-sm text-console-muted suse-light">Current distribution</p>
          </div>
          <div>
            <div className="h-48 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={nodeStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {nodeStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    content={<CustomPieTooltip />}
                    wrapperStyle={{ outline: 'none' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 mt-4">
              {nodeStatusData.map((item) => (
                <div key={item.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: item.color }}
                     />
                    <span className="text-console-muted">{item.name}</span>
                  </div>
                  <span className="text-console-text font-medium">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Status Row */}
      <div className="feature-grid mt-4">
        {/* Recent Nodes */}
        <div className="feature-card">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg suse-bold text-console-text">Recent Node Activity</h3>
              <p className="text-sm text-console-muted suse-light">Latest node status changes</p>
            </div>
            <Link to="/nodes" className="text-sm text-primary-400 hover:underline shrink-0">
              View all nodes
            </Link>
          </div>
          <div>
            <div className="space-y-4">
              {/* NodeInfo carries address (host:port), nodeId, status and lastSeen;
                  the API sends no name, port or uptime. */}
              {nodes.slice(0, 5).map((node) => (
                <div key={node.nodeId} className="flex items-center justify-between p-3 rounded-lg bg-secondary-800/30">
                  <div className="flex items-center gap-3">
                    <StatusIndicator status={node.status} showLabel={false} />
                    <div>
                      <div className="font-medium text-console-text">{node.address}</div>
                      <div className="text-sm text-console-muted">{node.nodeId}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-console-muted">{formatRelativeTime(node.lastSeen)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Alerts */}
        <div className="feature-card">
          <div className="mb-4">
            <h3 className="text-lg suse-bold text-console-text">Active Alerts</h3>
            <p className="text-sm text-console-muted suse-light">Requires immediate attention</p>
          </div>
          <div>
            <div className="space-y-4">
              {alerts.slice(0, 5).map((alert) => (
                <div key={alert.id} className="flex items-start gap-3 p-3 rounded-lg bg-secondary-800/30">
                  <AlertTriangle className="w-4 h-4 text-warning-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="severity" severity={alert.severity}>
                        {alert.severity}
                      </Badge>
                      <span className="text-xs text-console-muted">{formatRelativeTime(alert.timestamp)}</span>
                    </div>
                    <div className="font-medium text-console-text mb-1">{alert.title}</div>
                    <div className="text-sm text-console-muted line-clamp-2">{alert.message}</div>
                  </div>
                </div>
              ))}
              {alerts.length === 0 && (
                <div className="text-center py-8 text-console-muted">
                  <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No active alerts</p>
                  <p className="text-xs">All systems operating normally</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});